import "@preact/signals";
import * as preact from "preact";
import * as _ from "underscore";

import SearchQueryStore from "stores/SearchQueryStore";
import { ShelfData } from "stores/ShelvesStore";
import stores from "stores/stores";
import Select from "ui/Select";

interface Props {
  searchQuery: SearchQueryStore;
}

interface OptionData {
  displayName: string;
  value: string;
  count?: number;
  showCount?: boolean;
}

/** Options under a heading, or none for those at the top */
interface OptionGroup {
  label?: string;
  options: OptionData[];
}

const SEARCH_VALUE = "__searchQuery__";

class PrivateLibraryTagSelectorView extends preact.Component<Props> {
  computeOptionGroups(): OptionGroup[] {
    const { searchQuery } = this.props;
    const bookTagsViewJSON = stores.bookTags.viewJSON.value;
    const query = searchQuery.query.value;

    const tagOptions = (...names: string[]): OptionData[] =>
      _.compact(
        names.map((name) => _.findWhere(bookTagsViewJSON.tags, { name }))
      );

    const shelfOptions = stores.shelves.shelves.value.map((shelf) => ({
      displayName: shelf.name ?? "",
      count: shelf.bookCount ?? shelf.books.length,
      showCount: true,
      value: "shelf:" + shelf._id,
    }));

    return [
      {
        options: [
          ...(query.length > 0
            ? [
                {
                  displayName: "Search results",
                  value: SEARCH_VALUE,
                },
              ]
            : []),
          ...tagOptions("all"),
        ],
      },
      {
        label: "Read Status",
        options: tagOptions("queue", "completed", "abandoned"),
      },
      { label: "Shared", options: tagOptions("shared") },
      { label: "Shelves", options: shelfOptions },
      { label: "Deleted", options: tagOptions("deleted") },
    ].filter((group) => group.options.length > 0);
  }

  selectedValue(): string {
    const { searchQuery } = this.props;
    if (searchQuery.query.value.length > 0) {
      return SEARCH_VALUE;
    }
    const shelfID = stores.shelves.selectedShelfID.value;
    if (shelfID) {
      return "shelf:" + shelfID;
    }
    return stores.bookTags.activeTag.value ?? "";
  }

  changeTag = (selected: string) => {
    if (selected === SEARCH_VALUE) {
      return;
    }
    const shelfMatch = /^shelf:(.*)$/.exec(selected);

    this.props.searchQuery.setQuery("");

    if (shelfMatch && shelfMatch[1]) {
      if (shelfMatch[1] === "allTexts") {
        stores.shelves.selectedShelfID.value = null;
      } else {
        stores.shelves.selectedShelfID.value = shelfMatch[1];
      }
      stores.bookTags.activeTag.value = null;
    } else {
      stores.shelves.selectedShelfID.value = null;
      stores.bookTags.activeTag.value = selected;
    }
  };

  render() {
    const renderOption = (option: OptionData) => (
      <option key={option.value} value={option.value}>
        {option.displayName}
        {option.showCount ? ` (${option.count})` : ""}
      </option>
    );
    return (
      <Select
        aria-label="Show"
        value={this.selectedValue()}
        onChange={this.changeTag}
      >
        {this.computeOptionGroups().map((group) =>
          group.label ? (
            <optgroup key={group.label} label={group.label}>
              {group.options.map(renderOption)}
            </optgroup>
          ) : (
            group.options.map(renderOption)
          )
        )}
      </Select>
    );
  }
}

export default PrivateLibraryTagSelectorView;
